import React, { useState, useEffect } from "react";
import "./css/LoginFormModal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faShieldAlt,
  faDownload,
  faCopy,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import MfaAPI from "../services/MfaAPI";
import { useTranslation } from "../../../i18n/LanguageContext";

/**
 * Modal for MFA setup (enabling MFA)
 * Supports SMS and Email methods
 */
function MfaSetupModal({ onClose, onComplete, method = "sms" }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1: verify, 2: backup codes
  const [verificationCode, setVerificationCode] = useState("");
  const [setupSessionId, setSetupSessionId] = useState("");
  const [maskedDestination, setMaskedDestination] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [mfaMethod, setMfaMethod] = useState(method);
  const [theme, setTheme] = useState(
    document.documentElement.getAttribute("data-theme") || "light",
  );

  // Detect theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const currentTheme =
        document.documentElement.getAttribute("data-theme") || "light";
      setTheme(currentTheme);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // Auto-initiate MFA setup when modal opens
  useEffect(() => {
    initiateMfaSetup();
  }, []);

  const initiateMfaSetup = async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await MfaAPI.enableMfa(mfaMethod);
      console.log("MFA Enable Response:", response);
      console.log("Setup Session ID:", response.setupSessionId);
      setSetupSessionId(response.setupSessionId);
      setMaskedDestination(response.maskedPhone || "your verified email");
    } catch (err) {
      console.error("MFA Enable Error:", err);
      setError(
        err.response?.data?.error || err.error || t("mfaSetup.failedToSend"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!verificationCode.trim()) {
      setError(t("mfaSetup.verifyEnterCode"));
      return;
    }

    setIsLoading(true);

    try {
      const response = await MfaAPI.verifyMfaSetup(
        setupSessionId,
        verificationCode,
      );
      setBackupCodes(response.backupCodes || []);
      setStep(2);
    } catch (err) {
      setError(err.error || t("mfaSetup.verifyFailed"));
      setVerificationCode("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyCode = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleDownloadCodes = () => {
    const codesText = backupCodes.join("\n");
    const blob = new Blob([codesText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mfa-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleComplete = () => {
    onComplete();
  };

  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="login-modal" onClick={handleOutsideClick}>
      <div
        className="login-modal-content"
        data-theme={theme}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="close"
          onClick={onClose}
          aria-label={t("common.close")}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <h2>
          <FontAwesomeIcon
            icon={faShieldAlt}
            style={{ marginRight: "0.5rem" }}
          />
          {t("mfaSetup.title")}
        </h2>

        {/* Step 1: Verify Code */}
        {step === 1 && (
          <>
            {isLoading && !setupSessionId ? (
              <p style={{ textAlign: "center", margin: "2rem 0" }}>
                {t("mfaSetup.sendingCode")}
              </p>
            ) : !setupSessionId && error ? (
              <>
                <div className="error-message">{error}</div>
                <button
                  className="common-button primary"
                  style={{ width: "100%", marginTop: "1rem" }}
                  onClick={initiateMfaSetup}
                >
                  {t("mfaSetup.tryAgain")}
                </button>
              </>
            ) : (
              <>
                <p style={{ marginBottom: "1rem", textAlign: "center" }}>
                  {t("mfaSetup.codeSentTo")}{" "}
                  <strong>{maskedDestination}</strong>
                </p>
                <p
                  style={{
                    marginBottom: "1.5rem",
                    textAlign: "center",
                    fontSize: "0.9rem",
                    opacity: 0.8,
                  }}
                >
                  {mfaMethod === "email"
                    ? t("mfaSetup.emailHint")
                    : t("mfaSetup.smsHint")}
                </p>

                <form onSubmit={handleVerifySubmit}>
                  <div className="form-group">
                    <input
                      type="text"
                      maxLength="6"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, "");
                        setVerificationCode(value);
                        setError("");
                      }}
                      style={{
                        fontSize: "1.5rem",
                        textAlign: "center",
                        letterSpacing: "0.5rem",
                        fontFamily: "monospace",
                        padding: "1rem",
                      }}
                      disabled={isLoading}
                      autoFocus
                    />
                  </div>

                  {error && <div className="error-message">{error}</div>}

                  <button
                    type="submit"
                    className="common-button primary"
                    style={{ width: "100%", marginTop: "1rem" }}
                    disabled={isLoading || verificationCode.length !== 6}
                  >
                    {isLoading
                      ? t("mfaSetup.verifyingButton")
                      : t("mfaSetup.verifyButton")}
                  </button>
                </form>

                <p
                  style={{
                    textAlign: "center",
                    fontSize: "0.85rem",
                    marginTop: "1rem",
                    opacity: 0.7,
                  }}
                >
                  {t("mfaSetup.codeArrival")}
                </p>
              </>
            )}
          </>
        )}

        {/* Step 2: Backup Codes */}
        {step === 2 && (
          <>
            <div className="success-message" style={{ marginBottom: "1rem" }}>
              {t("mfaSetup.enabledMessage")}
            </div>

            <p style={{ marginBottom: "0.5rem", textAlign: "center" }}>
              {t("mfaSetup.saveCodesDesc")}
            </p>
            <p
              style={{
                marginBottom: "1.5rem",
                textAlign: "center",
                fontSize: "0.9rem",
                opacity: 0.8,
              }}
            >
              {t("mfaSetup.codesUsageDesc")}
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "0.75rem",
                marginBottom: "1.5rem",
                padding: "1rem",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                borderRadius: "8px",
                border: "1px solid rgba(0, 0, 0, 0.1)",
              }}
            >
              {backupCodes.map((code, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "0.75rem",
                    backgroundColor: "white",
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    borderRadius: "4px",
                    transition: "all 0.2s ease",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: "1rem",
                      fontWeight: "600",
                      letterSpacing: "0.1rem",
                    }}
                  >
                    {code}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopyCode(code, index)}
                    title={t("mfaSetup.copyCode")}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#1e3a8a",
                      cursor: "pointer",
                      padding: "0.25rem 0.5rem",
                      fontSize: "1rem",
                      transition: "all 0.2s ease",
                      borderRadius: "4px",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={copiedIndex === index ? faCheck : faCopy}
                    />
                  </button>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: "1rem" }}>
              <button
                type="button"
                className="common-button secondary"
                onClick={handleDownloadCodes}
                style={{ flex: 1 }}
              >
                <FontAwesomeIcon
                  icon={faDownload}
                  style={{ marginRight: "0.5rem" }}
                />
                {t("mfaSetup.downloadCodes")}
              </button>
              <button
                type="button"
                className="common-button primary"
                onClick={handleComplete}
                style={{ flex: 1 }}
              >
                {t("mfaSetup.done")}
              </button>
            </div>

            <div className="warning-message" style={{ marginTop: "1rem" }}>
              <strong>{t("mfaSetup.warningLabel")}</strong>{" "}
              {t("mfaSetup.warningText")}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MfaSetupModal;
